import { useApolloClient } from '@apollo/client/react';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { ROUTES } from '@repo/common';
import { clearPendingDestination, createLogger, readSignupIntent } from '@repo/frontend-shared/utils';

import { clearSignupAttribution } from '../utils/signup-attribution';
import { trackAnalyticsEvent } from './analytics-context';

const authTransitionLogger = createLogger('auth-transition');
const AUTH_TRANSITION_CHANNEL = 'starthub-auth-transition';
const authTransitionSource = crypto.randomUUID();

export const AUTH_TRANSITION_KIND = {
  SignedIn: 'signed-in',
  SignedOut: 'signed-out',
} as const;

export type AuthTransitionKind = (typeof AUTH_TRANSITION_KIND)[keyof typeof AUTH_TRANSITION_KIND];

interface AuthTransitionMessage {
  source: string;
  kind: AuthTransitionKind;
}

export function publishAuthTransition(kind: AuthTransitionKind): void {
  if (!('BroadcastChannel' in window)) return;

  const channel = new BroadcastChannel(AUTH_TRANSITION_CHANNEL);
  channel.postMessage({ source: authTransitionSource, kind } satisfies AuthTransitionMessage);
  channel.close();
}

function readTransitionKind(message: object): AuthTransitionKind | undefined {
  if (!('kind' in message)) return undefined;
  return Object.values(AUTH_TRANSITION_KIND).find(kind => kind === message.kind);
}

/**
 * Calls `onTransition` whenever another tab of this app signs in or out. The kind is undefined for a
 * message from a tab still running an older build.
 */
export function subscribeToOtherTabAuthTransitions(
  onTransition: (kind: AuthTransitionKind | undefined) => void,
): () => void {
  if (!('BroadcastChannel' in window)) return () => undefined;

  const channel = new BroadcastChannel(AUTH_TRANSITION_CHANNEL);
  channel.onmessage = event => {
    const message: unknown = event.data;
    if (
      typeof message !== 'object' ||
      message === null ||
      !('source' in message) ||
      message.source === authTransitionSource
    ) {
      return;
    }
    onTransition(readTransitionKind(message));
  };
  return () => channel.close();
}

export function handleOAuthFrontendCallback(resetStore: () => Promise<unknown>): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has('auth_success')) return false;

  urlParams.delete('auth_success');
  const registrationEventId = z.uuid().safeParse(urlParams.get('registration_event_id'));
  urlParams.delete('registration_event_id');
  const query = urlParams.toString();
  window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  // The backend already redirected to the requested destination; onboarding still carries it.
  if (window.location.pathname !== ROUTES.ONBOARDING) {
    clearPendingDestination();
  }
  if (registrationEventId.success) {
    clearSignupAttribution();
    trackAnalyticsEvent(
      'signup_completed',
      { method: 'oauth', signup_intent: readSignupIntent()?.intent ?? 'free' },
      { metaEventId: registrationEventId.data },
    );
  } else {
    trackAnalyticsEvent('login_completed', { method: 'oauth' });
  }
  resetStore().catch(error => {
    authTransitionLogger.error('Failed to reset Apollo state after OAuth login', error);
  });
  publishAuthTransition(AUTH_TRANSITION_KIND.SignedIn);
  return true;
}

export function useAuthTransitionListener(appInitialized: boolean): void {
  const apolloClient = useApolloClient();
  const resetPromise = useRef<Promise<void> | null>(null);
  const resetQueued = useRef(false);

  useEffect(() => {
    if (!appInitialized) return;

    const resetStore = (): void => {
      resetPromise.current = apolloClient
        .resetStore()
        .then(() => undefined)
        .catch(error => {
          authTransitionLogger.error('Failed to reset Apollo state after auth transition', error);
        })
        .finally(() => {
          resetPromise.current = null;
          if (resetQueued.current) {
            resetQueued.current = false;
            resetStore();
          }
        });
    };
    return subscribeToOtherTabAuthTransitions(() => {
      if (resetPromise.current) {
        resetQueued.current = true;
        return;
      }
      resetStore();
    });
  }, [apolloClient, appInitialized]);
}
